// Operation Atlas — country phase classification.
// Names match `feature.properties.name` in the world-atlas-countries-110m dataset.
// PHASE 1: large countries (~30) — easy to spot at default zoom, 10s timer.
// PHASE 2: medium countries (~50) — 8s timer.
// PHASE 3: small countries (~50) — 6s timer.
// PHASE 4: microstates / hard-to-spot (~40) — 4s timer.

export const PHASE_TIMERS = { 1: 10, 2: 8, 3: 6, 4: 4 };

export const PHASE_LABELS = {
  1: "Phase I · Recon",
  2: "Phase II · Sweep",
  3: "Phase III · Pinpoint",
  4: "Phase IV · Microstate",
};

// Streak thresholds → phase. Returns phase number 1..4.
export function phaseForStreak(streak) {
  if (streak >= 20) return 4;
  if (streak >= 10) return 3;
  if (streak >= 5)  return 2;
  return 1;
}

const TIER_1 = [
  "Russia","Canada","United States of America","China","Brazil","Australia",
  "India","Argentina","Kazakhstan","Algeria","Dem. Rep. Congo","Greenland",
  "Saudi Arabia","Mexico","Indonesia","Sudan","Libya","Iran","Mongolia","Peru",
  "Chad","Niger","Angola","Mali","South Africa","Colombia","Ethiopia",
  "Bolivia","Mauritania","Egypt","Tanzania",
];

const TIER_2 = [
  "Nigeria","Venezuela","Namibia","Mozambique","Pakistan","Turkey","Chile",
  "Zambia","Myanmar","Afghanistan","Somalia","Central African Rep.","Ukraine",
  "Madagascar","Botswana","Kenya","France","Yemen","Thailand","Spain","Turkmenistan",
  "Cameroon","Papua New Guinea","Sweden","Uzbekistan","Morocco","Iraq","Paraguay",
  "Zimbabwe","Japan","Germany","Republic of the Congo","Finland","Vietnam",
  "Malaysia","Norway","Poland","Oman","Italy","Philippines","Ecuador","Burkina Faso",
  "New Zealand","Gabon","Guinea","United Kingdom","Ghana","Romania","Laos","Uganda",
];

const TIER_3 = [
  "Guyana","Belarus","Kyrgyzstan","Senegal","Syria","Cambodia","Uruguay",
  "Suriname","Tunisia","Bangladesh","Tajikistan","Nepal","Greece","Nicaragua",
  "Eritrea","Korea","South Korea","Hungary","Jordan","Azerbaijan","Austria",
  "United Arab Emirates","Czechia","Serbia","Panama","Sierra Leone","Ireland",
  "Georgia","Sri Lanka","Lithuania","Latvia","Togo","Croatia","Bosnia and Herz.",
  "Slovakia","Estonia","Denmark","Netherlands","Switzerland","Bhutan","Taiwan",
  "Belgium","Lesotho","Armenia","Solomon Is.","Albania","Equatorial Guinea",
  "Burundi","Haiti","Rwanda","Macedonia","Djibouti","Belize","El Salvador",
  "Israel","Slovenia","Fiji","Kuwait","Eswatini","eSwatini",
];

const TIER_4 = [
  "Vanuatu","Qatar","Gambia","Jamaica","Lebanon","Cyprus","Brunei","Trinidad and Tobago",
  "Cabo Verde","Samoa","Luxembourg","Mauritius","Comoros","Bahrain","Singapore",
  "Tonga","Dominica","Andorra","Palau","Seychelles","Antigua and Barbuda","Barbados",
  "Saint Vincent and the Grenadines","Grenada","Malta","Maldives","Saint Kitts and Nevis",
  "Marshall Islands","Liechtenstein","San Marino","Tuvalu","Nauru","Monaco","Vatican",
  "S. Sudan","South Sudan","Bahamas","W. Sahara","Western Sahara","Falkland Is.","Fr. S. Antarctic Lands",
  "Saint Lucia","Kiribati","Micronesia","Sao Tome and Principe","Kosovo","Moldova","Montenegro",
];

const CLASS_MAP = new Map();
TIER_1.forEach(n => CLASS_MAP.set(n, 1));
TIER_2.forEach(n => CLASS_MAP.set(n, 2));
TIER_3.forEach(n => CLASS_MAP.set(n, 3));
TIER_4.forEach(n => CLASS_MAP.set(n, 4));

export function phaseOf(countryName) {
  return CLASS_MAP.get(countryName) || 2; // unknown → medium
}

/**
 * Pick a random country from the features dataset eligible for the given phase.
 * `features`: world-atlas geojson features. Each has feature.properties.name.
 * `excludeNames`: Set<string> — every country name already served this run. The
 *   picker will not return any of them until the eligible pool is exhausted;
 *   the caller is expected to reset the exclude set when this function
 *   returns null (i.e. every country in the phase's pool has been used).
 */
export function pickRandomCountry(features, phase, excludeNames) {
  if (!features || features.length === 0) return null;

  // Normalize the exclude argument to a Set for O(1) lookups. Historical
  // signature accepted a single string ("avoid last round only") — support
  // that for callers that haven't migrated yet.
  const exclude = excludeNames instanceof Set
    ? excludeNames
    : (excludeNames ? new Set([excludeNames]) : new Set());

  // Eligible pool: countries at or below the requested phase difficulty AND
  // not in the exclusion set.
  const pool = features.filter(f => {
    const name = f.properties?.name;
    if (!name || exclude.has(name)) return false;
    return phaseOf(name) <= phase;
  });

  if (pool.length === 0) {
    // Pool exhausted → return null so the caller can clear the history
    // (per the "history exclusion loop" spec) and retry.
    return null;
  }

  return pool[Math.floor(Math.random() * pool.length)];
}

export function countryNameOf(feature) {
  return feature?.properties?.name || "Unknown";
}
